import type { NostrEvent } from '@nostrify/nostrify';
import { decodeGeohash, isValidGeohash } from './geohash';

/** Parsed merchant listing from a kind 35341 event. */
export interface Merchant {
  /** The Nostr event. */
  event: NostrEvent;
  /** Pubkey of the merchant. */
  pubkey: string;
  /** Store name from `title` tag. */
  name: string;
  /** Optional summary from `summary` tag. */
  summary: string;
  /** Categories extracted from `t` tags (excluding 'bch-merchant'). */
  categories: string[];
  /** Website URL from `r` tag. */
  website: string;
  /** Raw geohash from `g` tag. */
  geohash: string;
  /** Decoded latitude/longitude, if geohash is valid. */
  location: { lat: number; lng: number } | null;
  /** Last heartbeat timestamp (unix seconds), matched by pubkey. */
  lastActive: number | null;
  /** Whether this merchant is verified by the admin. */
  verified: boolean;
  /** Whether this merchant has a paid sponsored listing. */
  sponsored: boolean;
  /** Which network the listing originated from. */
  source: 'bchpay' | 'bchnostr' | 'bch-local';
}

/** Known merchant categories with display labels and emoji. */
export const CATEGORIES: Record<string, { label: string; emoji: string }> = {
  cafe: { label: 'Cafe', emoji: '\u2615' },
  restaurant: { label: 'Restaurant', emoji: '\uD83C\uDF7D\uFE0F' },
  retail: { label: 'Retail', emoji: '\uD83D\uDED2' },
  grocery: { label: 'Grocery', emoji: '\uD83E\uDD66' },
  bar: { label: 'Bar', emoji: '\uD83C\uDF7A' },
  hotel: { label: 'Hotel', emoji: '\uD83C\uDFE8' },
  services: { label: 'Services', emoji: '\uD83D\uDD27' },
  entertainment: { label: 'Entertainment', emoji: '\uD83C\uDFAC' },
  health: { label: 'Health', emoji: '\uD83C\uDFE5' },
  education: { label: 'Education', emoji: '\uD83C\uDF93' },
  travel: { label: 'Travel', emoji: '\u2708\uFE0F' },
  other: { label: 'Other', emoji: '\uD83D\uDCCD' },
};

/** Get a tag value from an event. */
function getTag(event: NostrEvent, tagName: string): string {
  const tag = event.tags.find(([name]) => name === tagName);
  return tag?.[1] ?? '';
}

/** Get all values for a tag name. */
function getAllTags(event: NostrEvent, tagName: string): string[] {
  return event.tags.filter(([name]) => name === tagName).map(t => t[1] ?? '');
}

/** Parse a kind 35341 event into a Merchant object. */
export function parseMerchantEvent(event: NostrEvent): Merchant | null {
  // Require the bch-merchant tag
  const tTags = getAllTags(event, 't');
  if (!tTags.includes('bch-merchant')) return null;

  const name = getTag(event, 'title') || getTag(event, 'name');
  if (!name) return null;

  const summary = getTag(event, 'summary') || event.content || '';
  const categories = tTags.filter(t => t !== 'bch-merchant' && t !== 'bch-merchant-activity');
  const website = getTag(event, 'r');
  const geohash = getTag(event, 'g');

  let location: { lat: number; lng: number } | null = null;
  if (geohash && isValidGeohash(geohash)) {
    location = decodeGeohash(geohash);
  }

  return {
    event,
    pubkey: event.pubkey,
    name,
    summary,
    categories,
    website,
    geohash,
    location,
    lastActive: null,
    verified: false,
    sponsored: false,
    source: 'bchpay',
  };
}

/** Match heartbeat events (kind 7531) to merchants by pubkey. */
export function matchHeartbeats(
  merchants: Merchant[],
  heartbeats: NostrEvent[],
): Merchant[] {
  // Build a map of pubkey -> latest heartbeat created_at
  const heartbeatMap = new Map<string, number>();
  for (const hb of heartbeats) {
    const existing = heartbeatMap.get(hb.pubkey);
    if (!existing || hb.created_at > existing) {
      heartbeatMap.set(hb.pubkey, hb.created_at);
    }
  }

  return merchants.map(m => ({
    ...m,
    lastActive: heartbeatMap.get(m.pubkey) ?? m.lastActive,
  }));
}

/** Enrich merchants with verification status from a set of verified pubkeys. */
export function applyVerification(
  merchants: Merchant[],
  verifiedPubkeys: Set<string>,
): Merchant[] {
  return merchants.map(m => ({
    ...m,
    verified: verifiedPubkeys.has(m.pubkey),
  }));
}

/** Enrich merchants with sponsored status from a set of sponsored pubkeys. */
export function applySponsored(
  merchants: Merchant[],
  sponsoredPubkeys: Set<string>,
): Merchant[] {
  return merchants.map(m => ({
    ...m,
    sponsored: sponsoredPubkeys.has(m.pubkey),
  }));
}

/** Sort merchants: sponsored first, then verified, then the rest. */
export function sortMerchants(merchants: Merchant[]): Merchant[] {
  return [...merchants].sort((a, b) => {
    // Sponsored always first
    if (a.sponsored !== b.sponsored) return a.sponsored ? -1 : 1;
    // Then verified
    if (a.verified !== b.verified) return a.verified ? -1 : 1;
    // Then by name
    return a.name.localeCompare(b.name);
  });
}

/** Check if a merchant was active within the last N hours. */
export function isActiveWithin(merchant: Merchant, hours: number): boolean {
  if (!merchant.lastActive) return false;
  const cutoff = Math.floor(Date.now() / 1000) - hours * 3600;
  return merchant.lastActive >= cutoff;
}

/** Merchant activity status levels. */
export type MerchantStatus = 'active' | 'merchant' | 'new';

/**
 * Derive a merchant's status:
 * - 'active'   → has heartbeat activity within the last 24 hours (green)
 * - 'merchant' → listed and has had prior activity but none recently (yellow)
 * - 'new'      → newly listed with no heartbeat data yet (white)
 */
export function getMerchantStatus(merchant: Merchant): MerchantStatus {
  if (isActiveWithin(merchant, 24)) return 'active';
  if (merchant.lastActive !== null) return 'merchant';
  return 'new';
}

/** Display info for each merchant status. */
export const MERCHANT_STATUS_INFO: Record<
  MerchantStatus,
  { label: string; color: string; dotColor: string; description: string }
> = {
  active: {
    label: 'Active BCH Merchant',
    color: '#16a34a',        // green-600
    dotColor: '#22c55e',     // green-500
    description: 'Actively accepting BCH — heartbeat within 24 hours',
  },
  merchant: {
    label: 'BCH Merchant',
    color: '#ca8a04',        // yellow-600
    dotColor: '#eab308',     // yellow-500
    description: 'Accepts BCH — no recent activity signal',
  },
  new: {
    label: 'Newly Listed',
    color: '#6b7280',        // gray-500
    dotColor: '#d1d5db',     // gray-300
    description: 'Newly listed — awaiting activity confirmation',
  },
};

/** Format a relative time string from a unix timestamp. */
export function formatRelativeTime(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 604800)}w ago`;
}

/** Get category display info, with fallback. */
export function getCategoryInfo(category: string): { label: string; emoji: string } {
  return CATEGORIES[category.toLowerCase()] ?? {
    label: category.charAt(0).toUpperCase() + category.slice(1),
    emoji: '\uD83D\uDCCD',
  };
}
